import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  Row,
  Table,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import moment from 'moment';
import { getFormValues } from '../../../../helpers/forms';
import School from '../../../../services/School';
import PayrollAccountingCodes from '../../../../services/PayrollAccountingCodes';
import Attendances from '../../../../services/Attendances';
import { toast } from 'react-toastify';
import TimezonePicker from '../../../../components/Core/TimezonePicker';

interface BulkImportModalProps {
  isOpen: boolean;
  isFormOpen: boolean;
  toggle: () => void;
  toggleForm: () => void;
  searchStaffName: string;
  onChangeSearch: (value: string) => void;
  employees: any[];
  isSelectedAll: boolean;
  selectedStaff: any[];
  onToggleSelectAll: () => void;
  onSelectStaff: (employee: any) => void;
  payCode: string;
  setPayCode: (value: string) => void;
  payCodes: any[];
}

const BulkImportModal = ({
  isOpen,
  isFormOpen,
  toggle,
  toggleForm,
  searchStaffName,
  onChangeSearch,
  employees,
  isSelectedAll,
  selectedStaff,
  onToggleSelectAll,
  onSelectStaff,
  payCode: _payCode,
  setPayCode: _setPayCode,
  payCodes: _payCodes,
}: BulkImportModalProps) => {
  const { currentSchool, token, currentFranchisee } = useContext(AuthContext);
  const [school, setSchool] = useState<any>(null);
  const [payrollAccountingCodes, setPayrollAccountingCodes] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentSchool) {
      return;
    }
    const loadSchool = async () => {
      const result = await School(token || '').show(currentSchool.value);
      setSchool(result.data);
    };

    const loadPayrollAccountingCodes = async () => {
      const result = await PayrollAccountingCodes(token || '').list(currentFranchisee?.value);
      setPayrollAccountingCodes(result.data);
    };

    loadSchool();
    loadPayrollAccountingCodes();
  }, [currentSchool]);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;

    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    if (selectedStaff.length === 0) {
      toast.error('Please select at least one staff member');
      return;
    }

    try {
      setLoading(true);
      const formBody = getFormValues(e.target);

      const dateFormatted = moment(selectedDate, 'MM/DD/yyyy').format('YYYY-MM-DD');

      const userIds = selectedStaff.map((staff: any) => staff.user?.id || staff.id).filter(Boolean);

      if (userIds.length === 0) {
        toast.error('No valid user IDs found for selected staff');
        return;
      }

      const body = {
        dates: [dateFormatted],
        users: userIds,
        clockIn: formBody.checkIn,
        clockOut: formBody.checkOut,
        schoolId: currentSchool?.value || '',
        description: formBody.description || '',
        payCode: formBody.payrollAccountingCodeId || '',
      };

      await Attendances(token || '').bulkImport(body);
      toast.success('Bulk import completed successfully!');
      toggle();
      toggleForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to import attendances');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle} size={'xl'}>
      <div className="modal-header">
        <h4 className="modal-title">Bulk Payroll Import</h4>
        <div onClick={toggle} className="d-flex p-1 cursor-pointer">
          <i className="fas fa-times" />
        </div>
      </div>
      <div className="modal-body">
        {!isFormOpen ? (
          <div className="mt-4 p-2">
            <div className="d-flex align-items-center">
              <div className="w-100 d-flex justify-content-end">
                <Input
                  type="text"
                  placeholder="Search by Name"
                  style={{ width: '180px' }}
                  value={searchStaffName}
                  onChange={(event: any) => onChangeSearch(event.target.value)}
                />
              </div>
            </div>
            <Table className="mt-2" responsive>
              <tr>
                <th>
                  <Input
                    type="checkbox"
                    checked={isSelectedAll}
                    onChange={onToggleSelectAll}
                    style={{ transform: 'scale(1.5)' }}
                  />
                </th>
                <th>Staff Name</th>
                <th>Position</th>
                <th>Status</th>
                <th>90 days Employee?</th>
              </tr>
              {employees?.map((employee) => (
                <tr key={employee.id}>
                  <td>
                    <Input
                      type="checkbox"
                      checked={selectedStaff.find((staff: any) => staff.id === employee.id)}
                      onChange={() => onSelectStaff(employee)}
                      style={{ transform: 'scale(1.5)' }}
                    />
                  </td>
                  <td>{`${employee.user.firstName} ${employee.user.lastName}`}</td>
                  <td>{employee.department.name}</td>
                  <td>
                    {employee.workday === 'FT' && 'Full Time'}
                    {employee.workday === 'PT' && 'Part Time'}
                  </td>
                  <td>{moment() > moment(employee.startDate).add(90, 'days') ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </Table>
            <div className="d-flex justify-content-end mt-2">
              <Button style={{ background: '#02c412', color: '#FFFFFF', width: '150px' }} onClick={toggleForm}>
                Bulk Import
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 p-2">
            <Form onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e)}>
              <Row>
                <Col lg="4">
                  <FormGroup>
                    <label className="form-control-label">Date</label>
                    <InputGroup className="input-group-alternative">
                      <ReactDatetime
                        inputProps={{
                          placeholder: 'Please select',
                          name: 'date',
                        }}
                        timeFormat={false}
                        value={selectedDate}
                        onChange={(value: any) => {
                          if (moment(value).isValid()) {
                            setSelectedDate(moment(value).format('MM/DD/yyyy'));
                          }
                        }}
                        dateFormat={'MM/DD/yyyy'}
                        closeOnSelect={true}
                      />
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-calendar-grid-58" />
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col lg="4">
                  <FormGroup>
                    <label className="form-control-label">Check IN</label>
                    <TimezonePicker
                      name="checkIn"
                      onChangeTime={() => {}}
                      defaultValue={moment().format('YYYY-MM-DD') + ` ${school?.timeOperationStart}`}
                    />
                  </FormGroup>
                </Col>
                <Col lg="4">
                  <FormGroup>
                    <label className="form-control-label">Check OUT</label>
                    <TimezonePicker
                      name="checkOut"
                      onChangeTime={() => {
                        return;
                      }}
                      defaultValue={moment().format('YYYY-MM-DD') + ` ${school?.timeOperationEnd}`}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col lg="8">
                  <FormGroup>
                    <label className="form-control-label">Description</label>
                    <Input type="text" name="description" />
                  </FormGroup>
                </Col>
                <Col lg="4">
                  <FormGroup>
                    <label className="form-control-label">Accounting Code</label>
                    <Input type="select" name="payrollAccountingCodeId">
                      <option value=""></option>
                      {payrollAccountingCodes.map((a: any, index: number) => {
                        return (
                          <option value={a.id} key={`codes-${index}`}>
                            {a.name}
                          </option>
                        );
                      })}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <div className="d-flex justify-content-around">
                <Button className="btn-secondary" onClick={toggleForm}>
                  Cancel
                </Button>
                <Button
                  style={{ background: '#02c412', color: '#FFFFFF', width: '150px' }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkImportModal;
